/**
 * Builder do "Baixar Excel" do Fluxo de Caixa (#30) — função PURA, estilo
 * pldExport.
 *
 * A aba principal ("Fluxo diário") sai das MESMAS linhas que a tela usa
 * (`computeRows30d` — o array compartilhado do gráfico e do KPI "menor
 * saldo"): o xlsx bate o que o usuário vê POR CONSTRUÇÃO, sem re-agregar.
 * Colunas em NÚMERO (NumericCell R$/DateCell — SUM/AVG nativos no Excel).
 *
 * Modelo da tela (fiel, não inventado): barras = operacional (entradas Omie
 * CR / saídas CP Sync ou Omie); ENERGIA (net Zeus, com posição aberta CCEE
 * nos 6DUs) entra como degrau na LINHA de saldo — por isso a coluna própria.
 * Fluxo líquido do dia = entradas + saídas + energia (o degrau exato do
 * saldo acumulado).
 *
 * Abas: 1) Fluxo diário (janela do toggle 30/60/90); 2) Projeção 6 meses
 * (a tabela de períodos da tela); 3) Resumo (data-ref, janela, fonte das
 * saídas, totais). COMPOSIÇÃO por lançamento = follow-up (a tela não
 * carrega os lançamentos da janela inteira; sem endpoint novo aqui).
 * Sem PII — fluxo agregado por dia.
 */
import type { ExportColumn, ExtraSheet } from "../ui/ExportDropdown";
import type { Row } from "./FluxoBarChart";
import type { FluxoComposicaoResponse } from "../../api/types";
import type { ProjecaoMes } from "../../utils/mockData";
import { brl, dateCell, num } from "../../utils/xlsxHelpers";

export type FonteSaidas = "cp_sync" | "omie";

export interface FluxoExportInput {
  hojeIso: string; // data-ref (D+0) do /api/v2/fluxo
  dias: number; // janela do toggle (30/60/90)
  rows: Row[]; // computeRows30d(...) — o MESMO array da tela
  projecao: ProjecaoMes[]; // tabela de períodos (SaldoProjTable)
  saldoInicial: number; // saldo atual em conta (âncora do acumulado)
  fonteSaidas: FonteSaidas;
}

interface MainRow extends Record<string, unknown> {
  dataIso: string;
  entradas: number;
  saidas: number; // negativo (como a tela)
  energia: number;
  posicaoCcee: number;
  liquido: number;
  saldo: number;
}

export interface FluxoExportBuilt {
  filename: string; // sem extensão
  sheetName: string;
  mainColumns: ExportColumn<MainRow>[];
  mainRows: MainRow[];
  extraSheets: ExtraSheet[];
}

const FONTE_LABEL: Record<FonteSaidas, string> = {
  cp_sync: "CP do Sync (live)",
  omie: "Omie (CP indisponível — fallback)",
};

/** hojeIso + i dias, sem shift de timezone (mesma construção manual do chart). */
function _isoMaisDias(hojeIso: string, i: number): string {
  const [y, m, d] = hojeIso.split("-").map((n) => parseInt(n, 10));
  const cur = new Date(y, m - 1, d);
  cur.setDate(cur.getDate() + i);
  return `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;
}

export interface ComposicaoOpts {
  /** Abas de detalhe (#30b) — inseridas entre a projeção e o Resumo. */
  sheets?: ExtraSheet[];
  /** Nota da composição no Resumo (substitui o "follow-up" default). */
  nota?: string;
}

/** Constrói o pacote do export; null quando não há linha real pra exportar
 * (nunca exporta mock — o botão fica desabilitado). */
export function buildFluxoExport(
  input: FluxoExportInput,
  composicao?: ComposicaoOpts,
): FluxoExportBuilt | null {
  const { hojeIso, dias, rows, projecao, saldoInicial, fonteSaidas } = input;
  if (!rows.length) return null;

  const mainRows: MainRow[] = rows.map((r, i) => {
    const entradas = r.entradas_total ?? 0;
    const saidas = r.saidas_total ?? 0;
    const energia = r.energia_net ?? 0;
    return {
      dataIso: _isoMaisDias(hojeIso, i),
      entradas,
      saidas,
      energia,
      posicaoCcee: r.posicao_aberta_ccee ?? 0,
      // O degrau EXATO do saldo (mesma conta do _calc30dFromSeries).
      liquido: entradas + saidas + energia,
      saldo: r.saldo_cumulativo,
    };
  });

  const mainColumns: ExportColumn<MainRow>[] = [
    { key: "dataIso", header: "Data", format: (r) => dateCell(r.dataIso) },
    { key: "entradas", header: "Entradas operacionais (R$)", format: (r) => brl(r.entradas) },
    { key: "saidas", header: "Saídas operacionais (R$)", format: (r) => brl(r.saidas) },
    { key: "energia", header: "Energia net Zeus (R$)", format: (r) => brl(r.energia) },
    {
      key: "posicaoCcee",
      header: "— posição aberta CCEE (estimativa, já na energia)",
      format: (r) => brl(r.posicaoCcee),
    },
    { key: "liquido", header: "Fluxo líquido do dia (R$)", format: (r) => brl(r.liquido) },
    { key: "saldo", header: "Saldo acumulado (R$)", format: (r) => brl(r.saldo) },
  ];

  // ---- aba 2: Projeção 6 meses (tabela de períodos da tela) ----
  // F-30B: `projecao` vazia = fluxo mensal legado SEM dado real → a aba é
  // OMITIDA (nunca exporta a projeção mock) e o Resumo declara o buraco.
  const temProjecao = projecao.length > 0;
  type ProjRow = Record<string, unknown>;
  const projRows: ProjRow[] = projecao.map((p) => ({
    mes: p.mes, entradas: p.entradas, saidas: p.saidas,
    variacao: p.variacao, saldoFinal: p.saldoFinal,
  }));
  const projColumns: ExportColumn<ProjRow>[] = [
    { key: "mes", header: "Mês", format: (r) => String(r.mes) },
    { key: "entradas", header: "Entradas (R$)", format: (r) => brl(r.entradas as number) },
    { key: "saidas", header: "Saídas (R$)", format: (r) => brl(r.saidas as number) },
    { key: "variacao", header: "Variação (R$)", format: (r) => brl(r.variacao as number) },
    { key: "saldoFinal", header: "Saldo final (R$)", format: (r) => brl(r.saldoFinal as number) },
  ];

  // ---- aba 3: Resumo ----
  const somaEntradas = mainRows.reduce((s, r) => s + r.entradas, 0);
  const somaSaidas = mainRows.reduce((s, r) => s + r.saidas, 0);
  const somaEnergia = mainRows.reduce((s, r) => s + r.energia, 0);
  const saldoFinal = mainRows[mainRows.length - 1].saldo;
  type ResumoRow = Record<string, unknown>;
  const resumoRows: ResumoRow[] = [
    { campo: "Data de referência (D+0)", valor: dateCell(hojeIso) },
    { campo: "Janela", valor: `${dias} dias (D+0..D+${dias})` },
    { campo: "Saldo inicial (em conta)", valor: brl(saldoInicial) },
    { campo: "Fonte das saídas operacionais", valor: FONTE_LABEL[fonteSaidas] },
    { campo: "Total entradas operacionais", valor: brl(somaEntradas) },
    { campo: "Total saídas operacionais", valor: brl(somaSaidas) },
    { campo: "Total energia net (Zeus)", valor: brl(somaEnergia) },
    { campo: "Saldo final da janela", valor: brl(saldoFinal) },
    ...(temProjecao
      ? []
      : [{
          campo: "Projeção 6 meses",
          valor: "indisponível (fluxo mensal sem dado real — aba omitida)",
        }]),
    {
      campo: "Nota",
      valor:
        "Fluxo agregado por dia (mesmas séries da tela). " +
        (composicao?.nota ??
          "Composição por lançamento: indisponível neste arquivo."),
    },
  ];
  const resumoColumns: ExportColumn<ResumoRow>[] = [
    { key: "campo", header: "Campo", format: (r) => String(r.campo) },
    {
      key: "valor", header: "Valor",
      format: (r) => r.valor as string | number | null,
    },
  ];

  return {
    filename: `fluxo-caixa-${dias}du-${hojeIso}`,
    sheetName: "Fluxo diário",
    mainColumns,
    mainRows,
    extraSheets: [
      ...(temProjecao
        ? [{ name: "Projeção 6 meses", columns: projColumns as ExportColumn[], rows: projRows }]
        : []),
      ...(composicao?.sheets ?? []),
      { name: "Resumo", columns: resumoColumns as ExportColumn[], rows: resumoRows },
    ],
  };
}


// ---- Abas de COMPOSIÇÃO por lançamento (#30b) ---------------------------------

const POSICAO_LABEL = "POSIÇÃO ABERTA CCEE";

/** Monta as abas de detalhe a partir do /api/fluxo/composicao. Fiel às
 * colunas da aba "Fluxo diário":
 * - "Entradas energia (detalhe)": trades Zeus signed + a POSIÇÃO ABERTA CCEE
 *   como linha sintética rotulada estimativa → Σ por dia == coluna "Energia
 *   net Zeus" (mesma base da série, invariante do backend).
 * - "Saídas CP (detalhe)": itens do CP do Sync com valor NEGATIVO (o mesmo
 *   sinal da coluna "Saídas operacionais"). SÓ quando a tela usa o CP
 *   (fonteSaidas=cp_sync) — no fallback Omie a composição CP não corresponde
 *   à coluna e a aba é OMITIDA com nota (nunca detalhe que não soma). */
export function montarAbasComposicao(
  comp: FluxoComposicaoResponse,
  fonteSaidas: FonteSaidas,
): ComposicaoOpts {
  type CompRow = Record<string, unknown>;

  const energiaRows: CompRow[] = [
    ...comp.entradas_zeus.map((e) => ({
      data: e.data, contraparte: e.contraparte, tipo: e.tipo,
      submercado: e.submercado ?? "—",
      volume: e.volume_mwh, preco: e.preco_efetivo, valor: e.valor,
      obs: [
        e.ajuste_perna
          ? `valor e dia do título Omie ${e.ajuste_perna.titulo_omie} (Zeus R$ ${e.ajuste_perna.zeus_valor.toFixed(2)} em ${e.ajuste_perna.zeus_dt_pagamento}; #268)`
          : e.ajustado_pld ? "preço ajustado PLD (#213)" : "",
        e.cessao ?? "",
      ].filter(Boolean).join("; "),
    })),
    ...comp.posicao_aberta.map((p) => ({
      data: p.data, contraparte: POSICAO_LABEL, tipo: "estimativa",
      submercado: "—", volume: null, preco: null, valor: p.valor,
      obs: "marca PLD/forward — mesma estimativa do card 6DU",
    })),
  ].sort((a, b) => String(a.data).localeCompare(String(b.data)));
  const energiaCols: ExportColumn<CompRow>[] = [
    { key: "data", header: "Data (pagto)", format: (r) => dateCell(r.data as string) },
    { key: "contraparte", header: "Contraparte", format: (r) => String(r.contraparte) },
    { key: "tipo", header: "Tipo", format: (r) => String(r.tipo) },
    { key: "submercado", header: "Submercado", format: (r) => String(r.submercado) },
    { key: "volume", header: "Volume (MWh)", format: (r) => num(r.volume as number | null) },
    { key: "preco", header: "Preço efetivo (R$/MWh)", format: (r) => num(r.preco as number | null) },
    { key: "valor", header: "Valor (R$)", format: (r) => brl(r.valor as number) },
    { key: "obs", header: "Obs", format: (r) => String(r.obs ?? "") },
  ];

  const sheets: ExtraSheet[] = [
    { name: "Entradas energia (detalhe)", columns: energiaCols as ExportColumn[], rows: energiaRows },
  ];
  let nota = "Composição por lançamento: energia (Zeus) incluída.";

  if (fonteSaidas === "cp_sync") {
    const cpRows: CompRow[] = comp.saidas_cp.map((s) => ({
      data: s.data_previsao, fornecedor: s.fornecedor, categoria: s.categoria,
      documento: s.numero_documento ?? "—",
      vencimento: s.data_vencimento,
      // Mesmo sinal da coluna "Saídas operacionais" da aba diária (negativo).
      valor: s.valor === 0 ? 0 : -s.valor,
      // #264: título ausente da listagem da Omie SEGUE na soma — a linha diz isso
      // e há quantos dias. (O confirmado por id não está nesta aba: saiu das saídas.)
      existencia: s.existencia_rotulo
        ? `${s.existencia_rotulo}${s.dias_ausente != null ? ` (há ${s.dias_ausente} d)` : ""}`
        : "",
      // 13/10 PAR CESSIONARIA: a parcela segue na soma; a linha diz quem a paga.
      cessao: s.cessao ?? "",
    }));
    const cpCols: ExportColumn<CompRow>[] = [
      { key: "data", header: "Data prevista", format: (r) => dateCell(r.data as string) },
      { key: "fornecedor", header: "Fornecedor", format: (r) => String(r.fornecedor) },
      { key: "categoria", header: "Categoria", format: (r) => String(r.categoria) },
      { key: "documento", header: "Documento", format: (r) => String(r.documento) },
      { key: "vencimento", header: "Vencimento", format: (r) => dateCell(r.vencimento as string | null) },
      { key: "valor", header: "Valor (R$)", format: (r) => brl(r.valor as number) },
      { key: "existencia", header: "Existência na Omie (Sync)", format: (r) => String(r.existencia ?? "") },
      { key: "cessao", header: "Cessão (par cessionária)", format: (r) => String(r.cessao ?? "") },
    ];
    sheets.push({ name: "Saídas CP (detalhe)", columns: cpCols as ExportColumn[], rows: cpRows });
    nota = "Composição por lançamento: energia (Zeus) + saídas (CP do Sync) incluídas.";
  } else {
    nota =
      "Composição por lançamento: energia (Zeus) incluída; saídas em fallback " +
      "Omie — detalhe do CP omitido (não corresponde à coluna).";
  }

  return { sheets, nota };
}


// ---- Gate anti-mock do botão (F-30A/B/C) — função PURA, testável ------------

export interface MontarExcelArgs {
  v2Ok: boolean; // /api/v2/fluxo com dado e sem erro
  v2Loading: boolean; // transiente do toggle (F-30C)
  hojeIso: string | undefined;
  rows: Row[]; // computeRows30d da janela atual
  /** saldo REAL do /api/v2/saldo — undefined/null = sem saldo real (F-30A). */
  saldoReal: number | null | undefined;
  /** /api/fluxo legado com linhas reais (alimenta a projeção mensal). */
  legadoReal: boolean;
  projecao: ProjecaoMes[];
  dias: number;
  fonteSaidas: FonteSaidas;
}

/** Decide se o export está habilitado e com quê. Regra de ferro: NUNCA
 * exporta mock — saldo mock (F-30A) e projeção mock (F-30B) barram/degradam;
 * transiente de janela (F-30C) desabilita. `motivo` alimenta o title do
 * botão desabilitado (#246: desabilitado com motivo, não botão morto). */
export function montarExcelInput(
  a: MontarExcelArgs,
): { input: FluxoExportInput | null; motivo: string | null } {
  if (!a.v2Ok || !a.hojeIso) {
    return { input: null, motivo: "aguardando dado real do fluxo (não exporta mock)" };
  }
  if (a.v2Loading) {
    return { input: null, motivo: "atualizando a janela do gráfico…" };
  }
  if (a.saldoReal === null || a.saldoReal === undefined) {
    return { input: null, motivo: "aguardando saldo real (não exporta mock)" };
  }
  return {
    input: {
      hojeIso: a.hojeIso,
      dias: a.dias,
      rows: a.rows,
      // F-30B: projeção só quando o fluxo mensal legado é REAL; senão a aba
      // é omitida (o fluxo diário NÃO é bloqueado por causa dela).
      projecao: a.legadoReal ? a.projecao : [],
      saldoInicial: a.saldoReal,
      fonteSaidas: a.fonteSaidas,
    },
    motivo: null,
  };
}
